import { useEffect, useMemo, useState } from "react";
import { HOP_TIERS, type HopTierId } from "./api/types";
import { Background } from "./components/Background";
import { Graph } from "./components/Graph";
import { NodePanel } from "./components/NodePanel";
import { SearchBar } from "./components/SearchBar";
import { TopNav } from "./components/TopNav";
import { UsageStrip } from "./components/UsageStrip";
import { VaultPanel } from "./components/VaultPanel";
import { ZeroCommandCard } from "./components/ZeroCommandCard";
import { useSearch } from "./hooks/useSearch";
import { useWalletSession } from "./hooks/useWalletSession";

const useViewport = () => {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    const onResize = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
};

export const App = () => {
  const { width, height } = useViewport();
  const search = useSearch("hop1");
  const wallet = useWalletSession();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [vaultDraftSource, setVaultDraftSource] = useState<{
    url: string;
    title?: string | null;
  } | null>(null);

  const nodes = search.result?.nodes ?? [];
  const edges = search.result?.edges ?? [];
  const claims = search.result?.claims ?? [];
  const sources = search.result?.sources ?? [];
  const attribution = search.result?.attribution ?? [];

  const nodesById = useMemo(
    () => new Map(nodes.map((n) => [n.id, n])),
    [nodes],
  );
  const selected = selectedId ? (nodesById.get(selectedId) ?? null) : null;

  const handleSearch = (query: string, tier: HopTierId) => {
    setSelectedId(null);
    search.run(query, tier);
  };

  const tierMeta =
    HOP_TIERS.find((t) => t.id === (search.result?.tier ?? search.lastTier)) ??
    HOP_TIERS[1];

  const showEmptyState = !search.loading && !search.result && !search.error;
  const showNoResults =
    !search.loading && search.result && nodes.length === 0 && !search.error;

  return (
    <div className="noise relative w-screen h-screen overflow-hidden bg-void-100">
      <Background />
      <TopNav
        session={wallet.session}
        connecting={wallet.connecting}
        vaultCount={wallet.materializations.length}
        onConnect={wallet.connect}
        onDisconnect={wallet.disconnect}
        onOpenVault={() => {
          setVaultDraftSource(null);
          setVaultOpen(true);
        }}
      />

      {/* Graph canvas */}
      <div className="absolute inset-0">
        {nodes.length > 0 && (
          <Graph
            nodes={nodes}
            edges={edges}
            selectedId={selectedId}
            hoveredId={hoveredId}
            onSelect={setSelectedId}
            onHover={setHoveredId}
            width={width}
            height={height}
          />
        )}
      </div>

      {/* Empty / no-results overlay (centered, above the canvas) */}
      {(showEmptyState || showNoResults) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6">
          <div className="text-center max-w-xl animate-fade-up">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-flame-400/70 mb-4">
              provenance graph · for agents
            </div>
            <h1 className="font-display font-extrabold text-white tracking-tight leading-[0.95] text-5xl sm:text-6xl mb-6">
              search the graph.
              <br />
              <span className="text-flame-400">trace every claim.</span>
            </h1>
            <p className="text-white/55 text-base leading-relaxed mb-2">
              {showNoResults
                ? `no nodes for "${search.lastQuery}". try a broader query or a higher hop tier.`
                : "type a query above. results render as an explorable subgraph — click a node to see its summary, claims, sources, and contributor attribution."}
            </p>
            {search.result?.warnings && search.result.warnings.length > 0 && (
              <div className="mt-4 inline-flex flex-col gap-1 font-mono text-[11px] text-amber-300/80">
                {search.result.warnings.map((w) => (
                  <span key={w}>! {w}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error overlay */}
      {search.error && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 pointer-events-auto w-[min(92vw,620px)]">
          {search.paymentCommand ? (
            <ZeroCommandCard command={search.paymentCommand} />
          ) : (
            <div className="panel rounded-xl px-4 py-2 font-mono text-xs text-red-300/90 border-red-400/30">
              ! {search.error}
            </div>
          )}
        </div>
      )}

      {/* Top-center search */}
      <div className="absolute top-20 left-0 right-0 z-30 flex justify-center px-4 pointer-events-none">
        <div className="w-full max-w-3xl pointer-events-auto">
          <SearchBar
            onSearch={handleSearch}
            loading={search.loading}
            initialTier="hop1"
          />
        </div>
      </div>

      {/* Bottom usage strip */}
      {search.result && nodes.length > 0 && (
        <div className="absolute bottom-5 left-5 right-5 z-30 flex justify-center pointer-events-none">
          <div className="w-full max-w-4xl pointer-events-auto">
            <UsageStrip result={search.result} tier={tierMeta} />
          </div>
        </div>
      )}

      {/* Right side panel */}
      {selected && (
        <div className="absolute top-36 right-5 bottom-24 w-[380px] z-30 pointer-events-auto animate-fade-up">
          <NodePanel
            node={selected}
            edges={edges}
            nodesById={nodesById}
            claims={claims}
            sources={sources}
            attribution={attribution}
            onSelectNode={setSelectedId}
            onMaterializeSource={(source) => {
              if (!source.url) return;
              setVaultDraftSource({ url: source.url, title: source.title });
              setVaultOpen(true);
            }}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}

      {vaultOpen && (
        <div className="absolute top-20 left-5 bottom-24 w-[420px] z-40 pointer-events-auto animate-fade-up">
          <VaultPanel
            session={wallet.session}
            connecting={wallet.connecting}
            loading={wallet.vaultLoading}
            saving={wallet.saving}
            readerLoading={wallet.readerLoading}
            error={wallet.error}
            readerError={wallet.readerError}
            materializations={wallet.materializations}
            selectedMaterialization={wallet.selectedMaterialization}
            draftSource={vaultDraftSource}
            onConnect={wallet.connect}
            onClose={() => {
              wallet.closeMaterialization();
              setVaultOpen(false);
            }}
            onRefresh={wallet.refreshMaterializations}
            onSave={wallet.saveToVault}
            onOpenMaterialization={wallet.openMaterialization}
            onCloseMaterialization={wallet.closeMaterialization}
          />
        </div>
      )}
    </div>
  );
};
